import signupOffer from './signupOffer.json';
import { usd } from './format';
import { m } from '../paraglide/messages';

/** Canonical human-signup amounts and copy shared by marketing and lifecycle email. */
export const STARTER_BALANCE = signupOffer.starterBalance;
/**
 * Guarded by TestStarterRequestClaimMatchesCatalog in server/internal/registry, which
 * fails if catalog prices stop supporting the count the starting balance advertises.
 */
export const STARTER_REQUESTS = signupOffer.starterRequests;
/**
 * The one-time bonus each welcome-flow action pays (confirming the email,
 * following on X, joining Discord), on top of the starting balance.
 * `server/internal/httpapi/onboarding_bonus.go` credits the same figure per
 * action and its offer test binds the two, so the amount a customer is promised
 * is the amount the gateway moves.
 */
export const WELCOME_BONUS_BALANCE = signupOffer.welcomeGift;
/** How many welcome-flow actions pay that bonus. */
const WELCOME_BONUS_ACTIONS = 3;
/**
 * The free trial key an agent mints for itself with no human sign-in: its request
 * budget and how many days an unupgraded key lives. `server/internal/httpapi/agent_signup.go`
 * grants, caps and expires the same figures, and `TestAgentTrialMatchesPublishedOffer`
 * binds the two, so the trial an agent is promised is the trial the gateway mints.
 */
export const AGENT_TRIAL_BUDGET = signupOffer.agentTrialBudget;
export const AGENT_TRIAL_DAYS = signupOffer.agentTrialDays;
/**
 * Prose form of the count for running sentences ("that covers about 100 requests"),
 * where the bare `~100` reads wrong.
 */
export const STARTER_TYPICAL_REQUESTS = STARTER_REQUESTS.replace(/^~/, 'about ');

/**
 * How many requests one dollar of balance buys, derived from the starting offer
 * rather than typed: the offer is the only place that count is decided, and a
 * second number here would drift from it the moment the offer moves.
 */
const countIn = (text: string): number => Number(text.replace(/[^0-9.]/g, ''));
const REQUESTS_PER_USD = countIn(STARTER_REQUESTS) / countIn(STARTER_BALANCE);

/** The starting balance as a page prints it: `$0.10` in English, the local
 *  USD form (`US$ 0,10`) on a translated page. */
export const starterBalanceUsd = (): string => usd(countIn(STARTER_BALANCE));
/** The starting balance in dollars, for a sentence that divides it by a price. */
export const STARTER_BALANCE_USD = countIn(STARTER_BALANCE);

/** One action's welcome bonus in dollars, for the cards that name it before the
 *  gateway has answered. */
export const WELCOME_BONUS_USD = countIn(WELCOME_BONUS_BALANCE);

/** A USD amount as the request count a customer reads. Money is never shown to
 *  a new account in cents: a few cents reads as nothing, twenty requests reads
 *  as something they can spend. */
export const giftRequests = (usd: number): number => Math.round(usd * REQUESTS_PER_USD);

/**
 * What a customer who signs up and takes every welcome bonus ends up with: the
 * starting balance plus each action's bonus. This is the human offer -
 * the number the homepage, the signup buttons and the pre-signup pages print.
 *
 * `STARTER_REQUESTS` stays the grant on its own, because the lifecycle emails,
 * the agent skill and the blog all describe the balance a new account is handed
 * before it claims anything, and that sentence has to stay true.
 */
export const SIGNUP_OFFER_REQUESTS = `~${Math.round(
  (countIn(STARTER_BALANCE) + WELCOME_BONUS_ACTIONS * WELCOME_BONUS_USD) * REQUESTS_PER_USD,
)}`;
/**
 * The three signup phrases, in the locale of the render unless one is named.
 * Translated pages call this at render; the English constants below are the
 * same messages pinned to English, so the wording has one owner either way.
 * The hero drops the offer from its trust-bullet row and puts it on the button
 * itself (`HUMAN_SIGNUP_OFFER_CTA`), so the count still cannot drift from the
 * bullets elsewhere.
 */
export const signupPhrases = (options?: { locale?: 'en' }) => {
  const offer = m.signup_offer_free_requests({ count: SIGNUP_OFFER_REQUESTS }, options);
  return {
    HUMAN_SIGNUP_CTA: m.signup_offer_start_free({}, options),
    HUMAN_SIGNUP_OFFER: offer,
    HUMAN_SIGNUP_OFFER_CTA: m.signup_offer_cta({ offer }, options),
  };
};

const ENGLISH = signupPhrases({ locale: 'en' });
export const HUMAN_SIGNUP_CTA = ENGLISH.HUMAN_SIGNUP_CTA;
export const HUMAN_SIGNUP_OFFER = ENGLISH.HUMAN_SIGNUP_OFFER;
export const HUMAN_SIGNUP_OFFER_CTA = ENGLISH.HUMAN_SIGNUP_OFFER_CTA;
